import { SAW_ORBIT_PHASE_UNITS } from '@kenocyber/game-domain';
import type { PvpRuneWireKind } from '@kenocyber/shared';
import type { PvpClientState } from './connection';

/**
 * What the renderer draws, and when.
 *
 * ## Why interpolation exists at all
 *
 * The room broadcasts at most once per 50 ms tick, and a browser draws at 60,
 * 120 or 144 Hz. Drawing the newest frame verbatim would show a duel stepping
 * twenty times a second. So the renderer plays the authoritative world back
 * slightly behind real time and slides between the two most recent snapshots.
 *
 * ## The three rules this module exists to hold
 *
 * 1. **Never interpolate across a correction.** A rebuild can hand back a
 *    snapshot up to a second old, and a welcome can replace the world entirely.
 *    Sliding a ship from where it was to where it turns out to have been is a
 *    lie the player watches happen. The buffer is dropped and the new truth is
 *    shown immediately.
 * 2. **Never extrapolate.** Past the newest snapshot the renderer HOLDS. A
 *    guessed position is this client inventing a fight, and the room may simply
 *    have skipped that frame for backpressure - which is not the same as
 *    nothing having happened, and is certainly not something to guess about.
 * 3. **Only what the server sent exists.** A projectile absent from the newest
 *    snapshot is gone this instant, not faded out. One that appeared in it is
 *    drawn where the server says, not slid in from somewhere it never was.
 *
 * ## Time comes from the frames, never from the tick rate
 *
 * The interval between two snapshots is measured with the clock, not assumed to
 * be `tickMs`. State frames are skippable by design, so a gap of 50 ms and a
 * gap of 400 ms are both ordinary and both have to look right.
 */

/**
 * Who a ship IS, as far as the renderer is concerned.
 *
 * A string, and deliberately not `PvpSeat`. A seat is a position in a TWO-seat
 * room; a participant id is whoever the authority named. In a 1x1 the id IS the
 * seat - 'a' or 'b' - which is what makes this a widening rather than a
 * migration: every value the shipped duel produces is still a legal one here.
 *
 * Nothing about this type is a 3x3: it says the renderer stopped assuming how
 * many ships there are, and says nothing at all about a room that could serve
 * more than two.
 */
export type PvpParticipantId = string;

/**
 * A ship as the CLIENT state carries it - the wire's own frame, widened.
 *
 * {@link PvpShipFrame} satisfies this structurally: its `s` is `'a' | 'b'`,
 * which is a `PvpParticipantId`, and it simply has no `team`. So the socket
 * path is unchanged and needs no cast, while a caller that DOES know a team can
 * say so.
 *
 * `team` is optional and defaults to the participant's own id. That default is
 * exactly the 1x1: with one ship per team, "my team" and "me" are the same
 * string, which is why {@link PvpRenderShip} produced from a wire frame gives
 * precisely the roles and colours the two-player duel has always had.
 */
export interface PvpParticipantFrame {
  readonly s: PvpParticipantId;
  readonly team?: PvpParticipantId;
  readonly x: number;
  readonly y: number;
  readonly ax: number;
  readonly ay: number;
  /** A vida deste casco, em MILLI, como string decimal (gate V28G.S.1). */
  readonly hp: string;
  readonly c: boolean;
  /**
   * Tiques de EMPURRÃO que faltam a este casco (gate V28G.J).
   *
   * Opcional porque o frame o omite quando é zero, que é o estado normal. A
   * ausência diz 'obedece ao piloto'.
   */
  readonly kb?: number | undefined;
  /**
   * Quantas SERRAS orbitam este casco (gate V28G.P).
   *
   * Opcional porque o frame o omite quando não há nenhuma, que é o estado de
   * todo o casco sem esta joia e de todo o casco morto.
   */
  readonly sw?: number | undefined;
  /** A FASE autoritativa da órbita. Presente exatamente quando `sw` está. */
  readonly sf?: number | undefined;
  /**
   * **Os dois canhões temporários estão montados** (gate V28H.J).
   *
   * Opcional pela mesma razão que `sw` é: o frame omite-o na esmagadora maioria
   * dos casos, e a ausência quer dizer não. Público de propósito - os dois lados
   * desenham-nos, porque os dois lados levam tiros deles.
   */
  readonly sc?: 1 | undefined;
  /**
   * **Congelado por uma carta** (gate V28H.L). Ausente = não.
   *
   * Público, e tem de ser: um adversário que não visse o gelo não perceberia
   * porque a nave parou de responder. Quanto FALTA não vem - isso é informação
   * de decisão e viaja na metade privada da cadeira.
   */
  readonly fz?: 1 | undefined;
  /**
   * **Inalvejável por uma carta** (gate V28H.L). Ausente = não.
   *
   * Público pela razão gémea: sem ele, os tiros a atravessar uma nave leem-se
   * como um jogo partido em vez de uma defesa a funcionar.
   */
  readonly bt?: 1 | undefined;
  /**
   * **Os quatro estados de debuff** (gate V28H.W). Ausentes = nenhum.
   *
   * `mw` é o Malware, `pz` é a paralisia TOTAL do Congelamento, `lt` é a
   * Lentidão, e `ft` diz quanta casca de gelo desenhar - de um a três, pelo tempo
   * que falta. Todos públicos: quem enfrenta uma nave tem de perceber porque é
   * que ela parou, porque é que ela ficou lenta e porque é que a cura dela não
   * está a subir.
   */
  readonly mw?: 1 | undefined;
  readonly pz?: 1 | undefined;
  readonly lt?: 1 | undefined;
  readonly ft?: 1 | 2 | 3 | undefined;
  /** **Cadência partida pelo Curto-Circuito**, e **marcado pela Caveira** (V28H.X). */
  readonly cc?: 1 | undefined;
  readonly sk?: 1 | undefined;
  /**
   * **O que as cartas deste casco estão a desenhar** (gates V28H.L e V28H.O).
   *
   * `o` o círculo mágico passivo do Turbilhão, `b` a serra dele a cortar, `l` o
   * feixe da Lança Solar, `c` uma carga. Uma lista e não uma bandeira por carta:
   * as visíveis já são quatro e vão ser mais.
   */
  readonly cv?: readonly ('o' | 'b' | 'l' | 'c' | 'g' | 'd' | 'h' | 'k' | 'w' | 'm')[] | undefined;
  /**
   * Quanto da carga já foi cumprido, e com que força o feixe arde, em por cento.
   *
   * Os dois em número e não em bandeira porque é o VALOR que se desenha: a
   * corona cresce com o primeiro, e a espessura do feixe sai do segundo - a
   * mesma graduação com que a sala decide quem ele apanha.
   */
  readonly cg?: number | undefined;
  readonly cw?: number | undefined;
}

/** A projectile as the CLIENT state carries it. Same widening, same default. */
export interface PvpParticipantProjectileFrame {
  readonly i: number;
  readonly x: number;
  readonly y: number;
  readonly s: PvpParticipantId;
  readonly team?: PvpParticipantId;
  /** Este projétil é uma BOMBA (gate V28G.J). Ausente quer dizer que não é. */
  readonly b?: true | undefined;
  /**
   * E é o corpo de uma CARTA, e qual (gates V28H.U e V28H.W). Ausente = não é.
   *
   * `f` a Esfera Ígnea, `c` o Catolé, `z` a esfera do Congelamento, `t` a estrela
   * da Lentidão.
   */
  readonly cb?: 'f' | 'c' | 'z' | 't' | 's' | undefined;
  readonly sv?: 'd' | 'l' | undefined;
  /** O raio da explosão que ela vai abrir. Presente só com `cb`. */
  readonly br?: number | undefined;
}

/**
 * Um MÍSSIL como o estado do cliente o carrega (gate V28G.L).
 *
 * Uma forma própria e não uma bandeira num projétil, porque no fio também são
 * duas listas: um míssil não substitui um tiro nem se pendura num, e carrega uma
 * DIREÇÃO que um tiro não carrega.
 */
export interface PvpParticipantMissileFrame {
  readonly i: number;
  readonly x: number;
  readonly y: number;
  /** A direção autoritativa, normalizada em unidades do vetor da arena. */
  readonly dx: number;
  readonly dy: number;
  readonly s: PvpParticipantId;
  readonly team?: PvpParticipantId;
}

/**
 * **Um stack de veneno como o estado do cliente o carrega** (gate V28G.M).
 *
 * A MESMA forma que veio do fio, sem tradução: três inteiros e um id de casco.
 * Não há tipo de "render" separado porque não há nada a interpolar - ver a nota
 * em {@link PvpRenderFrame.poison}.
 */
export interface PvpParticipantPoisonFrame {
  /** A identidade do stack. Estável enquanto ele existir. */
  readonly i: number;
  /** O casco que o carrega. */
  readonly q: PvpParticipantId;
  /** Quantas das seis parcelas já foram cobradas. Sobe uma vez por segundo. */
  readonly n: number;
}

/**
 * **Uma marca de sangramento como o estado do cliente a carrega** (V28G.N).
 *
 * A MESMA forma que veio do fio, sem tradução: um id e dois cascos. Não há tipo
 * de "render" separado porque não há nada a interpolar - uma marca é uma
 * identidade e uma autoria, e o corte que ela produz é desenhado sobre o casco,
 * que já vem interpolado.
 */
export interface PvpParticipantBleedFrame {
  /** A identidade da marca. Estável enquanto ela existir. */
  readonly i: number;
  /** Quem a pôs, e o único que a pode detonar. */
  readonly p: PvpParticipantId;
  /** O casco que a carrega. */
  readonly q: PvpParticipantId;
}

/**
 * **Uma detonação confirmada sobre UM alvo** (gate V28G.N).
 *
 * Achatada por alvo à chegada, e não guardada como veio: o fio agrupa por ordem
 * - um autor, um tique, vários alvos - e a cena desenha um efeito por ALVO. A
 * chave já traz os três, portanto continua a deduplicar um frame repetido.
 *
 * Um EVENTO, e por isso vive numa fila drenável e nunca no frame de render: o
 * frame é reconstruído a cada quadro desenhado, e um efeito que vivesse lá seria
 * redesenhado sessenta vezes por segundo.
 */
export interface PvpParticipantBleedBurst {
  /** `(autor, tique, alvo)`. Deduplica um frame repetido ou uma correção. */
  readonly key: string;
  /** Quem mandou. */
  readonly p: PvpParticipantId;
  /** Em quem rebentou. */
  readonly q: PvpParticipantId;
  /** Quantas marcas rebentaram neste alvo. Decide quantas garras. */
  readonly n: number;
  /** O dano REALMENTE aplicado, em MILLI. O número impresso (V28G.S.1). */
  readonly d: bigint;
}

/**
 * **Uma REFLEXÃO confirmada, como o estado do cliente a carrega** (V28G.O).
 *
 * Um EVENTO, e por isso vive numa fila drenável e nunca no frame de render: o
 * frame é reconstruído a cada quadro desenhado, e um brilho que vivesse lá seria
 * redesenhado sessenta vezes por segundo.
 *
 * ## Porque as posições vêm no EVENTO e não são lidas dos cascos
 *
 * Porque são as posições do tique em que a interceção aconteceu, e um frame
 * carrega mais do que um tique. Ler os cascos ao desenhar daria as posições do
 * tique de PUBLICAÇÃO - que, a 20 Hz e com naves a mover-se, é outro sítio. O
 * feixe ligaria dois pontos onde nada aconteceu.
 */
export interface PvpParticipantReflect {
  /** `(refletor, destinatário, tique, payload, estágio)`. Deduplica um repetido. */
  readonly key: string;
  /** O REFLETOR: onde o brilho corre, e o novo autor do payload. */
  readonly p: PvpParticipantId;
  /** O destinatário: onde o feixe acaba, e onde o número é impresso. */
  readonly q: PvpParticipantId;
  /**
   * Que instância hostil foi intercetada.
   *
   * Uma string opaca para a cena: ela compara-a com `cannon_shot` para decidir
   * se desenha o feixe, e não conhece nenhum dos outros valores. Nunca o nome de
   * uma joia - uma cena que soubesse um seria uma cena a decorar um perfil.
   */
  readonly k: string;
  /** `i` acende o brilho; `l` imprime o número. */
  readonly s: 'i' | 'l';
  /** O refletor, em unidades de mundo, no tique da interceção. */
  readonly x: number;
  readonly y: number;
  /** O destinatário, no mesmo tique. */
  readonly tx: number;
  readonly ty: number;
  /**
   * O dano REALMENTE aplicado, em pontos inteiros. O número impresso.
   *
   * `null` em `i`, onde ainda não há dano nenhum: um zero seria "não doeu" em
   * vez de "ainda não aconteceu".
   */
  readonly d: bigint | null;
}

/**
 * **Uma invocação, como a cena a recebe** (gate V28H.L).
 *
 * A MESMA forma que veio do fio, sem tradução, como os projéteis e os mísseis:
 * ninguém a prediz e ninguém a interpola - ela chega onde a sala a pôs.
 */
export interface PvpRenderSummon {
  readonly i: number;
  readonly k: 'd' | 'l';
  readonly x: number;
  readonly y: number;
  readonly ax: number;
  readonly ay: number;
  /** De quem ela é. A cena pinta-a com a cor da equipa dele. */
  readonly p: string;
  /** A vida que resta, em basis points do máximo dela. */
  readonly hp: number;
  readonly pl?: string | undefined;
}

export interface PvpRenderShip {
  readonly seat: PvpParticipantId;
  /**
   * Which side this ship is on, as the AUTHORITY stated it.
   *
   * Resolved here rather than in the scene, and never guessed: a renderer that
   * worked out teams from an id prefix would be inventing an alliance. Equal to
   * `seat` whenever the frame did not carry one, which is the 1x1.
   */
  readonly team: PvpParticipantId;
  readonly x: number;
  readonly y: number;
  readonly aimX: number;
  readonly aimY: number;
  /**
   * Autoritativa, em MILLI. Never interpolated - a half-dealt hit point is not
   * a thing, e desde o gate V28G.S.1 um milésimo também não é.
   */
  readonly hpMilli: bigint;
  readonly connected: boolean;
  /**
   * Tiques de empurrão que faltam, e zero quando não há nenhum.
   *
   * Nunca interpolado: é uma contagem que o servidor possui, e um cliente que a
   * suavizasse mostraria um bloqueio que já tinha acabado. Vem do instantâneo
   * MAIS NOVO, como a vida vem.
   */
  readonly knockbackTicks: number;
  /**
   * Quantas serras orbitam este casco, ou `undefined` (gate V28G.P).
   *
   * Do instantâneo mais NOVO e nunca interpolado: a contagem é um facto que a
   * sala declara, e não há nada entre duas e três lâminas para misturar.
   */
  readonly sw?: number | undefined;
  /**
   * A FASE autoritativa da órbita, ou `undefined` (gate V28G.P).
   *
   * ## Porque ela NÃO é interpolada, ao contrário da posição
   *
   * Porque é um ângulo modular, e uma média de dois ângulos que atravessaram o
   * zero aponta para o lado errado: entre 350 e 10 graus, a interpolação linear
   * dá 180. A lâmina anda 25 passos por tique no nível 30 - meio grau por
   * quadro a 60 Hz - e a órbita lê-se contínua sem que ninguém misture nada.
   */
  readonly sf?: number | undefined;
  /** Os dois canhões temporários, do instantâneo mais novo (gate V28H.J). */
  readonly sc?: 1 | undefined;
  /**
   * **Congelado por uma carta** (gate V28H.L). Ausente = não.
   *
   * Público, e tem de ser: um adversário que não visse o gelo não perceberia
   * porque a nave parou de responder. Quanto FALTA não vem - isso é informação
   * de decisão e viaja na metade privada da cadeira.
   */
  readonly fz?: 1 | undefined;
  /**
   * **Inalvejável por uma carta** (gate V28H.L). Ausente = não.
   *
   * Público pela razão gémea: sem ele, os tiros a atravessar uma nave leem-se
   * como um jogo partido em vez de uma defesa a funcionar.
   */
  readonly bt?: 1 | undefined;
  /**
   * **Os quatro estados de debuff** (gate V28H.W). Ausentes = nenhum.
   *
   * `mw` é o Malware, `pz` é a paralisia TOTAL do Congelamento, `lt` é a
   * Lentidão, e `ft` diz quanta casca de gelo desenhar - de um a três, pelo tempo
   * que falta. Todos públicos: quem enfrenta uma nave tem de perceber porque é
   * que ela parou, porque é que ela ficou lenta e porque é que a cura dela não
   * está a subir.
   */
  readonly mw?: 1 | undefined;
  readonly pz?: 1 | undefined;
  readonly lt?: 1 | undefined;
  readonly ft?: 1 | 2 | 3 | undefined;
  /** **Cadência partida pelo Curto-Circuito**, e **marcado pela Caveira** (V28H.X). */
  readonly cc?: 1 | undefined;
  readonly sk?: 1 | undefined;
  /**
   * **O que as cartas deste casco estão a desenhar** (gates V28H.L e V28H.O).
   *
   * `o` o círculo mágico passivo do Turbilhão, `b` a serra dele a cortar, `l` o
   * feixe da Lança Solar, `c` uma carga. Uma lista e não uma bandeira por carta:
   * as visíveis já são quatro e vão ser mais.
   */
  readonly cv?: readonly ('o' | 'b' | 'l' | 'c' | 'g' | 'd' | 'h' | 'k' | 'w' | 'm')[] | undefined;
  /**
   * Quanto da carga já foi cumprido, e com que força o feixe arde, em por cento.
   *
   * Os dois em número e não em bandeira porque é o VALOR que se desenha: a
   * corona cresce com o primeiro, e a espessura do feixe sai do segundo - a
   * mesma graduação com que a sala decide quem ele apanha.
   */
  readonly cg?: number | undefined;
  readonly cw?: number | undefined;
}

export interface PvpRenderProjectile {
  readonly id: number;
  readonly x: number;
  readonly y: number;
  readonly seat: PvpParticipantId;
  /** Its owner's team, so an ally's fire never reads as incoming. */
  readonly team: PvpParticipantId;
  /**
   * Este projétil é uma BOMBA (gate V28G.J).
   *
   * Um facto que a AUTORIDADE declara, nunca inferido pela velocidade entre dois
   * instantâneos - uma inferência dessas erra em todos os frames que chegam
   * atrasados, que são precisamente aqueles em que o buffer existe.
   */
  readonly bomb: boolean;
  /** De que ESTRUTURA ele saiu, ou nulo se saiu de um casco (gate V28H.X). */
  readonly fromSummon: 'drone' | 'leviathan' | null;
  /**
   * **A bomba de uma CARTA, e qual** (gate V28H.U).
   *
   * `null` num tiro comum e na Bomba Atômica da joia, que continua a ser `bomb`
   * e a ser desenhada como sempre foi. Declarado pela autoridade como `bomb` é, e
   * nunca deduzido do tamanho: deduzir a arte de um raio seria trocá-la no dia
   * em que alguém afinasse o número.
   */
  readonly cardBlast: 'fireball' | 'catole' | 'ice_sphere' | 'ice_star' | 'shock_bolt' | null;
  /**
   * O raio da explosão que ela vai abrir, em unidades de mundo, ou `null`.
   *
   * O corpo desenhado é uma fracção dele, e é por isso que a esfera de uma carga
   * curta se vê pequena e a de uma carga cheia se vê grande - o que se vê é o
   * que apanha.
   */
  readonly blastRadius: number | null;
}

/**
 * Um míssil, pronto a desenhar (gate V28G.L).
 *
 * A posição é interpolada como a de um tiro; a DIREÇÃO não é. Ela vem do
 * instantâneo mais novo tal e qual: é uma verdade que o servidor possui, e
 * misturar duas direções produziria um ângulo que nenhum tique teve - visível
 * num objeto com nariz, e invisível numa bala redonda.
 */
export interface PvpRenderMissile {
  readonly id: number;
  readonly x: number;
  readonly y: number;
  /**
   * A DIREÇÃO autoritativa, tal e qual - e nunca um ângulo já feito.
   *
   * O ângulo é obra da cena, através da única função de orientação que existe
   * (`missileRotation`). Convertê-lo aqui poria uma segunda resposta à mesma
   * pergunta no caminho, e a orientação do míssil já esteve errada uma vez por
   * causa disso.
   */
  readonly dirX: number;
  readonly dirY: number;
  readonly seat: PvpParticipantId;
  readonly team: PvpParticipantId;
}

export interface PvpRenderFrame {
  readonly ships: readonly PvpRenderShip[];
  /**
   * As invocações a existir (gate V28H.L). Ausente = nenhuma.
   *
   * Sem interpolação: elas chegam com a posição que a sala lhes deu, como os
   * projéteis - e ao contrário de um casco, ninguém as prediz.
   */
  readonly summons?: readonly PvpRenderSummon[];
  readonly projectiles: readonly PvpRenderProjectile[];
  /** Os mísseis em voo (gate V28G.L). Uma lista à parte, como no fio. */
  readonly missiles: readonly PvpRenderMissile[];
  /**
   * What each seat is under the influence of, straight from the room.
   *
   * NOT interpolated. A timer is a count the server owns, and a client that
   * eased one between frames would show an effect that had already expired.
   * Taken from the newest snapshot rather than the pair being blended.
   */
  readonly effects: readonly PvpRenderEffects[];
  /** Every pad and what is on it. Positions are a shared constant. */
  readonly runes: readonly PvpRenderRune[];
  /**
   * Os stacks de veneno ATIVOS, do instantâneo mais NOVO e nunca misturados
   * (gate V28G.M).
   *
   * ## Porque não é interpolado
   *
   * Porque não há nada aqui que seja uma posição. Um stack é uma contagem e uma
   * identidade; a marca que ele produz é desenhada sobre o casco, e é o CASCO
   * que se move - e esse já vem interpolado. Misturar `n` entre dois
   * instantâneos daria um número fracionário de parcelas, que nenhum tique teve.
   *
   * É a mesma regra que `effects` segue, pela mesma razão exata.
   */
  readonly poison: readonly PvpParticipantPoisonFrame[];
  /**
   * As marcas de sangramento ATIVAS, do instantâneo mais NOVO e nunca misturadas
   * (gate V28G.N).
   *
   * Pela mesma razão que `poison` não é interpolado: não há aqui nada que seja
   * uma posição. Uma marca é uma identidade e uma autoria; o corte que ela produz
   * é desenhado sobre o casco, e é o CASCO que se move.
   */
  readonly bleeding: readonly PvpParticipantBleedFrame[];
  /** The authoritative tick of the OLDER snapshot being drawn towards. */
  readonly tick: number;
  readonly msLeft: number;
  /**
   * Bumped whenever the world was replaced discontinuously. A renderer that
   * pools sprites uses it to drop trails, tweens and anything else that assumes
   * the previous frame was the previous instant.
   */
  readonly correction: number;
}

export interface PvpRenderEffects {
  readonly seat: PvpParticipantId;
  readonly doubleDamageTicks: number;
  readonly speedTicks: number;
  readonly teleportCharges: number;
}

export interface PvpRenderRune {
  readonly pad: number;
  readonly kind: PvpRuneWireKind | null;
}

interface Snapshot {
  /** When this landed on this machine. Used to size the buffer, never to place it. */
  readonly at: number;
  /** When it happened IN THE ROOM: the tick, in milliseconds. */
  readonly roomAt: number;
  readonly tick: number;
  readonly ships: readonly PvpParticipantFrame[];
  /** As invocações a existir (gate V28H.L). */
  readonly summons?: readonly PvpRenderSummon[];
  readonly projectiles: readonly PvpParticipantProjectileFrame[];
  readonly missiles: readonly PvpParticipantMissileFrame[];
  readonly effects: readonly PvpRenderEffects[];
  readonly runes: readonly PvpRenderRune[];
  readonly poison: readonly PvpParticipantPoisonFrame[];
  readonly bleeding: readonly PvpParticipantBleedFrame[];
  readonly msLeft: number;
  readonly correction: number;
}

/**
 * How far behind the newest snapshot playback sits, to begin with.
 *
 * Interpolation needs a snapshot on both sides of the instant being drawn. When
 * the next one has not arrived the opponent stops where it is, so this has to
 * cover the gap between arrivals - and every millisecond past that is pure
 * added lag on where the enemy appears to be, which is what a player leads
 * their shots against.
 *
 * Measured in a real duel, once the intent cadence stopped starving the room's
 * clock: authoritative frames arrive every 60 ms at the median and 93 ms at the
 * 95th percentile. A hundred is the first round number that covers the 95th.
 *
 * It does not have to stay there. The buffer grows it when playback actually
 * runs out of frames and gives it back when it stops - see
 * {@link PvpViewBuffer.requiredMs} - so this is where a match opens and where a
 * healthy connection returns to, not a bet on every connection.
 *
 * It was 75, chosen when frames were arriving in 214 ms lumps, against a signal
 * that turned out to be broken. Six duels with both values measured in both
 * orders to rule out warm-up: 9.6% of the opponent's frames frozen at 75 ms
 * against 2.8% at 100 ms.
 */
export const PVP_RENDER_DELAY_MS = 100;

/**
 * How many authoritative snapshots the buffer keeps.
 *
 * Two was not enough and that was the whole freeze. With two, the only pair to
 * slide between is the last two frames, so the playback target has to land
 * inside a 50 ms window that moves in 50 ms steps - and any jitter at all puts
 * it past the newest sample, where `clamp01` pins it and the ship stops until
 * the next frame arrives. Measured on the player's recording: 19% of rendered
 * frames showed no movement, then a jump of up to 20.6 px.
 *
 * Six covers a late burst with an older sample still to slide from, and is
 * bounded so a long match cannot grow it. The buffer drops the oldest beyond
 * this.
 *
 * It also has to cover the delay. The cursor sits one delay behind the newest
 * snapshot and is clamped to the oldest one still held, so a history shorter
 * than {@link PVP_MAX_RENDER_DELAY_MS} would pin the cursor at its own oldest
 * frame exactly when the delay had grown to cope with a bad connection - the
 * moment the slack is needed most.
 *
 * Six is the smallest number that does: five gaps of one tick is 250 ms of
 * room time in the worst case, where every frame carries a single tick,
 * against a ceiling of 200 ms. The delay suite states that relation as an
 * assertion so moving either constant cannot quietly break the other.
 */
export const PVP_HISTORY_LIMIT = 6;

/**
 * The longest gap the renderer will slide across.
 *
 * Past this the two snapshots are not two moments of one motion - they are two
 * separate situations with a hole between them, and drawing a smooth line
 * through the hole invents the part nobody sent. It snaps instead.
 */
export const PVP_MAX_INTERPOLATION_MS = 500;

/**
 * The floor and the ceiling the playback delay may move between.
 *
 * The floor is one server tick: below that the newest sample is always in the
 * future of the playback point and there is nothing to slide towards. The
 * ceiling is where added lag stops being worth the smoothness - 200 ms is
 * 68 arena units of stale enemy position, over two and a half ship radii, which
 * is as much as aiming can be asked to absorb.
 */
export const PVP_MIN_RENDER_DELAY_MS = 50;
export const PVP_MAX_RENDER_DELAY_MS = 200;

/**
 * Kept in hand above where playback would otherwise sit.
 *
 * Ten milliseconds is under a fifth of a tick: enough that the ordinary
 * scheduling wobble between one arrival and the next does not empty the buffer,
 * small enough that a well behaved connection barely pays for it.
 */
export const PVP_DELAY_MARGIN_MS = 10;

/**
 * How fast the delay may move, as a fraction of real time.
 *
 * Growth is the faster of the two on purpose: running out of buffer costs a
 * freeze now, while carrying too much delay only costs lag.
 */
export const PVP_DELAY_GROWTH_RATE = 0.5;
export const PVP_DELAY_DECAY_RATE = 0.2;

/**
 * How far playback may run from one second per second while it steers.
 *
 * ## Why playback has a cursor at all
 *
 * Two obvious answers were built and measured, and both are wrong.
 *
 * **Interpolating between the moments the snapshots ARRIVED.** Two frames that
 * describe 50 ms of simulation and land 25 ms apart are then replayed over
 * 25 ms - the whole world at double speed - and two that land 93 ms apart are
 * replayed at half. Every wobble in delivery becomes a wobble in how fast the
 * duel appears to run. Measured in a real duel: every projectile on screen
 * stepping exactly twice its own median distance in the same frame. A
 * projectile has one constant velocity from muzzle to impact, so it was the
 * playback that sped up, not the shots.
 *
 * **Placing playback on the room's own clock.** A snapshot says when it
 * happened - its tick - and ticks are exactly {@link PvpArena.tickMs} apart, so
 * sequencing by them fixes the pacing exactly. It also assumes the room's clock
 * keeps up with the wall, and it does not: the room advances on incoming
 * messages and refuses to simulate through a gap longer than
 * {@link PVP_MAX_CATCHUP_MS}, so every stall costs it time it never gets back.
 * Measured over four seconds of duel, the room fell 583 ms behind local time -
 * about 85% of real time. Anchoring to it left 203 of 448 rendered frames
 * clamped at the newest sample, moving at exactly the right speed in between.
 * The pacing was right and the buffer was empty.
 *
 * So playback keeps its OWN cursor, in room time. It advances by real elapsed
 * time - which is what makes it smooth, since nothing about a rendered frame
 * depends on when a packet landed - and it is steered, gently, to stay one
 * delay behind the newest snapshot. Steering absorbs both the room running slow
 * and the network running late, without either of them being modelled.
 *
 * A quarter is the most it will deviate: the opponent briefly drifting or
 * hurrying by a quarter, never jumping. It also has to exceed the rate the room
 * loses time at - 15% was measured - or the cursor could never catch a room
 * running slow.
 */
export const PVP_CURSOR_RATE_LIMIT = 0.25;

/**
 * How much error makes the cursor steer at its full rate.
 *
 * The correction is proportional: half this much error steers at half the
 * limit. Two hundred milliseconds is four ticks, so an ordinary wobble is
 * nudged and a real divergence is chased.
 */
export const PVP_CURSOR_CORRECTION_MS = 200;

export class PvpViewBuffer {
  /** The two most recent authoritative snapshots. Oldest first. */
  private history: Snapshot[] = [];
  /**
   * The playback delay in force right now, which MOVES.
   *
   * The delay has one job: keep a snapshot on the far side of the playback
   * cursor, so there is always something to slide towards. Any fixed number is
   * a bet on a distribution - measured against the gaps a real duel produced,
   * 75 ms leaves 15.9% of frames frozen on a 144 Hz client and 100 ms leaves
   * 2.5%, but add a single 140 ms gap, which a real network will, and 100 ms
   * leaves 10.6% and even 150 ms leaves 1.4%. There is no value both small
   * enough to stay responsive and large enough for every tail.
   *
   * So it is measured at runtime: how far behind the newest snapshot the cursor
   * is actually sitting, plus a margin, bounded at both ends and rate-limited.
   * A good connection is played back close to live; one that starts stuttering
   * buys itself slack automatically and gives it back when it recovers.
   */
  private delayMs: number;
  /** The value it starts at, and returns towards when arrivals are punctual. */
  private readonly baseDelayMs: number;
  /** When the delay was last moved, so the rate limit is per real millisecond. */
  private lastAdaptedAt: number | null = null;
  /**
   * Whether the last sample found the cursor pressed against the newest
   * snapshot, with nothing on the far side to slide towards.
   *
   * Reported so a measurement can attribute a jump: a client that ran out of
   * buffer and then caught up is a network or scheduling story, not a
   * presentation defect, and the two look identical from outside.
   */
  private starved = false;
  /** The instant being drawn, in ROOM time. See {@link PVP_CURSOR_RATE_LIMIT}. */
  private cursorMs: number | null = null;
  /** When the cursor last advanced, so it can advance by real elapsed time. */
  private lastCursorAt: number | null = null;
  /**
   * The delay starvation has shown to be necessary, decayed while none happens.
   *
   * Starvation is observable and specific: the cursor advancing past the newest
   * snapshot, which only happens when the room stopped speaking while playback
   * kept going. The amount it overshot by is exactly the delay that was
   * missing, so this remembers it and gives it back slowly.
   *
   * The obvious alternative - size the delay from how far behind the newest
   * snapshot the cursor is sitting - measures the delay itself. The cursor is
   * steered to sit one delay behind, so that reading is always the delay, and
   * feeding it back drives the value to its ceiling on a perfect connection.
   * Measured: 200 ms, the maximum, on a feed with no jitter at all.
   */
  private requiredMs: number | null = null;
  /** Fixed by the caller: a measurement asking for one delay gets that delay. */
  private readonly adaptive: boolean;
  /**
   * Also injected, and for the same reason.
   *
   * The freeze the player reported came from the PAIR of these: a two-sample
   * window with a playback point one tick behind. `view.delay.test.ts` builds a
   * buffer with the original numbers to reproduce it, and would have no way to
   * do that if either were a constant.
   */
  private readonly limit: number;

  constructor(
    delayMs: number = PVP_RENDER_DELAY_MS,
    limit: number = PVP_HISTORY_LIMIT,
    adaptive = true,
  ) {
    this.delayMs = delayMs;
    this.baseDelayMs = delayMs;
    this.limit = Math.max(2, limit);
    this.adaptive = adaptive;
  }

  /** What the delay is right now. Reported so a test can watch it move. */
  currentDelayMs(): number {
    return this.delayMs;
  }

  /** True while playback has caught up with the newest frame and is waiting. */
  isStarved(): boolean {
    return this.starved;
  }

  /** When the newest snapshot arrived, so a measurement can age it. */
  newestArrivalAt(): number | null {
    return this.history.at(-1)?.at ?? null;
  }

  /**
   * The newest AUTHORITATIVE snapshot, unblended.
   *
   * Beside `isStarved` and `newestArrivalAt` because it answers the same kind
   * of question: what did the room last actually say, as opposed to what is
   * being drawn towards. Nothing in the render path uses it - the scene draws
   * from `sample` - but a measurement that needs the server's own numbers,
   * rather than an interpolated pair, has to be able to ask.
   */
  newest(): {
    readonly tick: number;
    readonly effects: readonly PvpRenderEffects[];
    readonly runes: readonly PvpRenderRune[];
  } | null {
    const snapshot = this.history.at(-1);
    if (!snapshot) {
      return null;
    }
    return { tick: snapshot.tick, effects: snapshot.effects, runes: snapshot.runes };
  }

  /**
   * Where the delay wants to be: the worst lag lately, plus the margin.
   *
   * The lag is measured, not modelled - how far behind the newest snapshot the
   * cursor actually sat on recent frames. A connection whose packets are all
   * late but evenly spaced produces a steady lag and needs no extra delay; one
   * that alternates early and late produces a varying lag, and the worst of it
   * is exactly what has to be covered.
   */
  private desiredDelayMs(): number {
    return Math.min(
      Math.max(this.requiredMs ?? this.baseDelayMs, this.baseDelayMs, PVP_MIN_RENDER_DELAY_MS),
      PVP_MAX_RENDER_DELAY_MS,
    );
  }

  /** Where playback currently sits, in room time. Reported so a test can watch it. */
  cursorRoomMs(): number | null {
    return this.cursorMs;
  }

  /**
   * Advances the cursor by real elapsed time, steered towards where it belongs.
   *
   * The advance is what makes playback smooth: it depends on the frame's own
   * elapsed time and on nothing that arrived over a network. The steering is
   * what keeps it correct, and it is bounded, so the correction is a drift
   * rather than a jump.
   */
  private advanceCursor(now: number, newest: Snapshot, oldest: Snapshot): void {
    const wanted = newest.roomAt - this.delayMs;
    if (this.cursorMs === null) {
      this.cursorMs = wanted;
      this.lastCursorAt = now;
      return;
    }
    const previous = this.lastCursorAt;
    this.lastCursorAt = now;
    const elapsed = previous === null ? 0 : Math.max(0, now - previous);
    const error = wanted - this.cursorMs;
    const steer = Math.max(
      -PVP_CURSOR_RATE_LIMIT,
      Math.min(PVP_CURSOR_RATE_LIMIT, error / PVP_CURSOR_CORRECTION_MS),
    );
    const raw = this.cursorMs + elapsed * (1 + steer);
    /*
     * Never past the newest sample, and never behind what the buffer still
     * holds. Beyond the newest there is nothing to slide towards; before the
     * oldest the frames it needs have already been dropped.
     */
    this.cursorMs = Math.max(oldest.roomAt, Math.min(raw, newest.roomAt));

    /*
     * How much delay was missing, if any. An overshoot means playback caught up
     * with the room and had to wait - the one thing more delay would have
     * prevented, and the only evidence worth acting on.
     */
    const overshoot = raw - newest.roomAt;
    this.starved = overshoot > 0;
    if (overshoot > 0) {
      this.requiredMs = Math.max(
        this.requiredMs ?? this.baseDelayMs,
        this.delayMs + overshoot + PVP_DELAY_MARGIN_MS,
      );
      return;
    }
    if (this.requiredMs !== null) {
      this.requiredMs = Math.max(
        this.baseDelayMs,
        this.requiredMs - elapsed * PVP_DELAY_DECAY_RATE,
      );
    }
  }

  private adapt(now: number): void {
    if (!this.adaptive) {
      return;
    }
    const previous = this.lastAdaptedAt;
    this.lastAdaptedAt = now;
    if (previous === null) {
      return;
    }
    const elapsed = now - previous;
    if (elapsed <= 0) {
      return;
    }
    const desired = this.desiredDelayMs();
    if (desired > this.delayMs) {
      this.delayMs = Math.min(desired, this.delayMs + elapsed * PVP_DELAY_GROWTH_RATE);
      return;
    }
    this.delayMs = Math.max(desired, this.delayMs - elapsed * PVP_DELAY_DECAY_RATE);
  }

  private lastCorrection = 0;

  /**
   * Takes an authoritative state, if it is a new one.
   *
   * Called on every client-state change, which includes changes that are not
   * about the world at all - a phase moving, a failure being recorded. Those
   * must not push a duplicate snapshot, or the buffer would fill with copies of
   * one instant and the renderer would stop moving.
   */
  push(state: PvpClientState, receivedAt: number): void {
    if (state.arena === null) {
      return;
    }
    if (state.correction !== this.lastCorrection) {
      /*
       * A seam. The world was replaced rather than advanced - a welcome, or a
       * tick that went backwards because the room was rebuilt from a snapshot
       * up to a second old.
       *
       * Everything buffered describes a world that no longer follows from this
       * one, so it is dropped. The next sample shows the new truth at once: a
       * visible jump, which is honest, rather than a smooth slide to a position
       * the ship is retroactively discovered to have been in.
       */
      this.history = [];
      /*
       * And the cursor. A rebuilt room can resume from a snapshot up to a
       * second old, so its ticks no longer follow the old ones - a cursor kept
       * across the seam would be a second in the wrong place, and would spend
       * seconds steering back.
       */
      this.cursorMs = null;
      this.lastCursorAt = null;
      this.requiredMs = null;
      this.lastCorrection = state.correction;
    }
    const newest = this.history.at(-1);
    if (newest && newest.tick === state.tick && newest.ships === state.ships) {
      // The same frame, delivered again by an unrelated state change.
      return;
    }

    const roomAt = state.tick * state.arena.tickMs;

    this.history.push({
      at: receivedAt,
      roomAt,
      tick: state.tick,
      ships: state.ships,
      /*
       * **As invocações, que este buffer esquecia** (gate V28H.P).
       *
       * O campo estava declarado no instantâneo e nunca era escrito. O tipo
       * é opcional - porque um frame sem invocações não o traz - portanto o
       * compilador nunca o exigiu, `pvpInterpolate` lia `undefined`, e a cena
       * desenhava uma lista vazia.
       *
       * O dono premiu a tecla, viu o drone disparar, viu o adversário levar
       * dano, e não viu drone nenhum: "o visual do mob em si não aparece". Ele
       * existia na sala, atravessava o fio, chegava ao estado do cliente - e
       * morria aqui, uma linha antes de ser desenhado.
       */
      summons: state.summons ?? [],
      projectiles: state.projectiles,
      missiles: state.missiles,
      effects: state.effects,
      runes: state.runes,
      poison: state.poison,
      bleeding: state.bleeding,
      msLeft: state.msLeft,
      correction: state.correction,
    });
    while (this.history.length > this.limit) {
      this.history.shift();
    }
  }

  /** Drops everything. Used when a scene is torn down or a match is left. */
  reset(): void {
    this.history = [];
    this.lastCorrection = 0;
    // The delay goes back to where it started: a new match is not the old
    // match's connection, and it must not inherit the slack it needed.
    this.delayMs = this.baseDelayMs;
    this.lastAdaptedAt = null;
    this.cursorMs = null;
    this.lastCursorAt = null;
    this.requiredMs = null;
  }

  /**
   * What to draw at this instant, or `null` if nothing authoritative has
   * arrived yet.
   *
   * A renderer that got `null` must draw no ships at all rather than draw them
   * somewhere plausible - there is no plausible place for a ship whose position
   * the server has not stated.
   */
  sample(now: number): PvpRenderFrame | null {
    const newest = this.history.at(-1);
    if (!newest) {
      return null;
    }
    if (this.history.length < 2) {
      // One snapshot: nothing to slide between. Draw it exactly.
      return frameOf(newest, newest, 0);
    }

    /*
     * The cursor advances, and the pair that SURROUNDS it is what gets drawn.
     *
     * Picking the surrounding pair - rather than always the oldest and newest -
     * is what makes a deeper history useful: the playback point keeps moving
     * smoothly through the buffer instead of being clamped at the end of a
     * two-sample window every time a frame is late.
     */
    this.adapt(now);
    this.advanceCursor(now, newest, this.history[0] as Snapshot);
    const target = this.cursorMs as number;
    let from = this.history[0];
    let to = this.history[1];
    for (let i = 0; i < this.history.length - 1; i += 1) {
      const candidate = this.history[i];
      const next = this.history[i + 1];
      if (candidate && next && candidate.roomAt <= target && next.roomAt >= target) {
        from = candidate;
        to = next;
        break;
      }
      if (next && next.roomAt <= target) {
        // Still behind the target: the newest pair is the best available.
        from = candidate;
        to = next;
      }
    }
    if (!from || !to) {
      return frameOf(newest, newest, 0);
    }

    const span = to.roomAt - from.roomAt;
    if (span <= 0 || span > PVP_MAX_INTERPOLATION_MS) {
      // Two situations with a hole between them, not two moments of one motion.
      return frameOf(newest, newest, 0);
    }
    /*
     * CLAMPED at both ends. Below zero would draw before a moment already
     * shown; above one would extrapolate past the newest sample, guessing where
     * a ship went when the room may simply have skipped a frame.
     */
    const t = clamp01((target - from.roomAt) / span);
    return frameOf(from, to, t);
  }
}

/**
 * A letra do fio, lida como o nome do corpo (gates V28H.U e V28H.W).
 *
 * Uma função e não um encadeado de ternários no meio da interpolação: são quatro
 * corpos e vão ser mais, e um ternário de quatro braços é onde o quinto se
 * engana.
 */
function cardBlastOf(
  letter: 'f' | 'c' | 'z' | 't' | 's' | undefined,
): 'fireball' | 'catole' | 'ice_sphere' | 'ice_star' | 'shock_bolt' | null {
  switch (letter) {
    case 'c':
      return 'catole';
    case 'z':
      return 'ice_sphere';
    case 't':
      return 'ice_star';
    case 's':
      return 'shock_bolt';
    case 'f':
      return 'fireball';
    default:
      return null;
  }
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) {
    return 1;
  }
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

function lerp(from: number, to: number, t: number): number {
  return Math.round(from + (to - from) * t);
}

/**
 * **A fase da órbita das serras, interpolada pelo caminho CURTO** (V28G.P).
 *
 * ## O defeito que isto corrige
 *
 * A sala publica no máximo uma vez por tique - 50 ms - e o cliente desenha a 60
 * quadros por segundo. A posição do casco já era interpolada entre dois
 * instantâneos, portanto a nave andava macia; a fase não era, portanto as
 * lâminas ficavam paradas três quadros e saltavam 25 graus ao quarto. Ao nível
 * 30 uma volta dura 720 ms, e 50 ms disso é exatamente esse salto.
 *
 * ## Porque um `lerp` simples não servia, e o que serve
 *
 * Porque a fase é MODULAR: entre 41 000 e 700 a interpolação linear anda para
 * trás pelo caminho longo, e a serra dava meia-volta uma vez por revolução. O
 * que se interpola é o DELTA desenrolado - o caminho curto entre os dois - e
 * depois enrola-se outra vez.
 *
 * O desenrolamento é EXATO e não uma adivinhação: a órbita anda sempre no mesmo
 * sentido, e o avanço máximo entre dois frames publicados é de 2 900 unidades no
 * nível 30, contra as 20 880 de meia volta. Nunca há ambiguidade sobre por onde
 * ela foi.
 *
 * ## Isto não inventa movimento
 *
 * É a mesma disciplina da posição do casco: dois instantâneos AUTORITATIVOS e
 * uma reamostragem para o relógio de desenho. Não há velocidade adivinhada, não
 * há extrapolação para lá do frame novo - `t` vive em `[0, 1]` - e a colisão
 * continua a acontecer na fase autoritativa. O que muda é só a que ritmo o mesmo
 * movimento é mostrado.
 */
export function lerpSawPhase(from: number, to: number, t: number): number {
  const half = SAW_ORBIT_PHASE_UNITS / 2;
  let delta =
    (((to - from) % SAW_ORBIT_PHASE_UNITS) + SAW_ORBIT_PHASE_UNITS) % SAW_ORBIT_PHASE_UNITS;
  if (delta > half) {
    delta -= SAW_ORBIT_PHASE_UNITS;
  }
  const raw = Math.round(from + delta * t);
  return ((raw % SAW_ORBIT_PHASE_UNITS) + SAW_ORBIT_PHASE_UNITS) % SAW_ORBIT_PHASE_UNITS;
}

function frameOf(from: Snapshot, to: Snapshot, t: number): PvpRenderFrame {
  /*
   * **As invocações são INTERPOLADAS entre dois instantâneos** (gate V28H.X).
   *
   * Elas vinham do instantâneo mais recente, sem mistura - "ninguém as prediz" -
   * e isso estava certo enquanto uma invocação era um drone que ninguém
   * conduzia. Deixou de estar quando o Leviatã passou a ser PILOTADO: quem o
   * conduz vê-o a saltar de cinquenta em cinquenta milésimos, com a câmara
   * agarrada a ele, e a arena inteira salta junto.
   *
   * A EXISTÊNCIA continua a ser a do instantâneo novo - uma invocação que a
   * sala removeu desaparece no mesmo quadro, e não meio quadro depois - e o que
   * se mistura é só ONDE ela está: a mesma disciplina dos cascos, com o mesmo
   * `t` e a mesma reamostragem. O rumo não se interpola, pela razão de sempre:
   * misturar dois ângulos dá um que nenhum tique teve.
   */
  const summons = (to.summons ?? []).map((summon) => {
    const previous = from.summons?.find((other) => other.i === summon.i);
    if (previous === undefined) {
      /* Nasceu depois do instantâneo antigo: desenha-se onde ela está. */
      return summon;
    }
    return {
      ...summon,
      x: Math.round(lerp(previous.x, summon.x, t)),
      y: Math.round(lerp(previous.y, summon.y, t)),
    };
  });
  const ships = to.ships.map((ship) => {
    const previous = from.ships.find((other) => other.s === ship.s);
    if (!previous) {
      // A seat the older snapshot did not carry. Drawn where it is.
      return renderShip(ship, ship, 0);
    }
    return renderShip(previous, ship, t);
  });

  const projectiles: PvpRenderProjectile[] = [];
  for (const projectile of to.projectiles) {
    const previous = from.projectiles.find((other) => other.i === projectile.i);
    if (!previous) {
      /*
       * Born since the older snapshot, so it did not exist at the instant being
       * drawn. It waits.
       *
       * Drawing it at the newer snapshot's position instead - which is what
       * this did - pins it there for the whole span, because every frame in the
       * pair finds no previous and returns the same point. Measured in a real
       * duel: fifteen shots, twenty-nine rendered frames with a projectile
       * standing still, and a largest step of exactly twice the median. Two
       * frames stopped at the muzzle and then a double-length jump, per shot.
       *
       * The cost is that a projectile which lives inside a single snapshot is
       * never drawn at all - a contact shot, sixty-two arena units from the
       * cannon, inside a ship's own radius. That is the honest trade: a shot
       * the player never had time to see, against every shot hanging at the
       * muzzle before it leaves.
       */
      continue;
    }
    projectiles.push({
      id: projectile.i,
      x: lerp(previous.x, projectile.x, t),
      y: lerp(previous.y, projectile.y, t),
      seat: projectile.s,
      // Its own id when the frame named no team, which is the 1x1 exactly.
      team: projectile.team ?? projectile.s,
      /* Do frame, e nunca deduzido: um tiro comum omite o campo. */
      bomb: projectile.b === true,
      /* E a salva de uma estrutura, pela mesma regra (gate V28H.X). */
      fromSummon: projectile.sv === 'l' ? 'leviathan' : projectile.sv === 'd' ? 'drone' : null,
      /* E as duas da carta, pela mesma regra (gate V28H.U). */
      cardBlast: cardBlastOf(projectile.cb),
      blastRadius: projectile.br ?? null,
    });
  }

  /*
   * Os mísseis, pela mesma regra de interpolação dos tiros (gate V28G.L).
   *
   * Um míssil nascido depois do instantâneo mais antigo ESPERA, exatamente como
   * um tiro espera, e pela mesma razão medida: desenhá-lo na posição do
   * instantâneo mais novo prendia-o lá durante todo o intervalo.
   *
   * O RUMO não é interpolado. Ele vem da direção autoritativa do instantâneo mais
   * novo: misturar dois ângulos produziria um que nenhum tique teve, e num objeto
   * com nariz isso vê-se. `Math.atan2` aqui é APRESENTAÇÃO - decide para onde um
   * sprite aponta e mais nada.
   */
  const missiles: PvpRenderMissile[] = [];
  for (const missile of to.missiles) {
    const previous = from.missiles.find((other) => other.i === missile.i);
    if (!previous) {
      continue;
    }
    missiles.push({
      id: missile.i,
      x: lerp(previous.x, missile.x, t),
      y: lerp(previous.y, missile.y, t),
      dirX: missile.dx,
      dirY: missile.dy,
      seat: missile.s,
      team: missile.team ?? missile.s,
    });
  }

  return {
    ships,
    summons,
    // Exactly the newest snapshot's list. A shot the server stopped sending is
    // gone this instant: it hit something, expired, or the match ended.
    projectiles,
    missiles,
    /*
     * From the NEWEST snapshot, never blended.
     *
     * A timer is a count the room owns and an effect either applies on a tick
     * or does not; easing one between two frames would put a number on screen
     * that no tick ever had, and for a teleport charge there is nothing
     * between nought and one to show.
     */
    effects: to.effects,
    runes: to.runes,
    /* Do mais NOVO, como os efeitos e pela mesma razão (gate V28G.M). */
    poison: to.poison,
    bleeding: to.bleeding,
    tick: to.tick,
    msLeft: to.msLeft,
    correction: to.correction,
  };
}

function renderShip(from: PvpParticipantFrame, to: PvpParticipantFrame, t: number): PvpRenderShip {
  return {
    seat: to.s,
    /*
     * The NEWER frame's team, and its own id when there is none.
     *
     * Never blended and never remembered from the older sample: a side is a
     * fact the room states, not a quantity, and there is nothing between two
     * teams to interpolate.
     */
    team: to.team ?? to.s,
    x: lerp(from.x, to.x, t),
    y: lerp(from.y, to.y, t),
    aimX: lerp(from.ax, to.ax, t),
    aimY: lerp(from.ay, to.ay, t),
    /*
     * Health is taken whole, from the newest snapshot.
     *
     * Sliding it would draw a bar at a value the server never reported, and
     * would make a ship look alive for a moment after the room says it is not.
     * Damage is discrete; the bar is allowed to jump.
     */
    hpMilli: BigInt(to.hp),
    connected: to.c,
    /* Do instantâneo mais novo, como a vida. Zero é 'ninguém o empurra'. */
    knockbackTicks: to.kb ?? 0,
    /*
     * As serras (gate V28G.P), e as duas metades têm regras DIFERENTES.
     *
     * A CONTAGEM vem do instantâneo mais novo, inteira: é um facto que a sala
     * declara, e não há nada entre duas e três lâminas para misturar.
     *
     * A FASE é interpolada, pelo caminho curto - ver {@link lerpSawPhase}. Sem
     * isso a órbita andava à cadência de publicação da sala, 20 vezes por
     * segundo, e a 60 quadros isso lê-se como a serra a saltar.
     *
     * Os campos são omitidos quando não há lâminas, e a omissão sobrevive: um
     * `0` explícito faria uma cena distinguir "sem serras" de "sem serras" duas
     * vezes. A fase só é interpolada quando o instantâneo VELHO também a tinha -
     * uma nave que acabou de ganhar lâminas não vem de lado nenhum.
     */
    ...(to.sw === undefined ? {} : { sw: to.sw }),
    /*
     * Os canhões NÃO se interpolam: eles existem ou não existem, e um estado
     * intermédio seria meio canhão. O valor do instantâneo mais recente é o que
     * vale, exatamente como a contagem das lâminas.
     */
    ...(to.sc === undefined ? {} : { sc: to.sc }),
    /*
     * As duas bandeiras das cartas sobrevivem sem serem misturadas, como `sc`:
     * um casco está congelado ou não está, e não há meio caminho entre dois
     * instantâneos.
     */
    ...(to.fz === undefined ? {} : { fz: to.fz }),
    ...(to.bt === undefined ? {} : { bt: to.bt }),
    /* E os quatro do gelo e do Malware, pela mesma regra (gate V28H.W). */
    ...(to.mw === undefined ? {} : { mw: to.mw }),
    ...(to.pz === undefined ? {} : { pz: to.pz }),
    ...(to.lt === undefined ? {} : { lt: to.lt }),
    ...(to.ft === undefined ? {} : { ft: to.ft }),
    /* E os dois do gate V28H.X. */
    ...(to.cc === undefined ? {} : { cc: to.cc }),
    ...(to.sk === undefined ? {} : { sk: to.sk }),
    /*
     * A lista de desenhos sobrevive SEM ser misturada, como as bandeiras: uma
     * lâmina está a girar ou não está, e não há meio caminho entre dois
     * instantâneos.
     */
    ...(to.cv === undefined ? {} : { cv: to.cv }),
    /*
     * A carga e a força do feixe também NÃO são misturadas.
     *
     * A carga é uma contagem que sobe, e interpolá-la entre dois quadros daria
     * uma corona a andar para trás sempre que um quadro chegasse fora de ordem.
     * A força é fixada no instante da soltura e nunca mais muda - misturar duas
     * leituras dela seria inventar uma terceira que nunca existiu.
     */
    ...(to.cg === undefined ? {} : { cg: to.cg }),
    ...(to.cw === undefined ? {} : { cw: to.cw }),
    ...(to.sf === undefined
      ? {}
      : { sf: from.sf === undefined ? to.sf : lerpSawPhase(from.sf, to.sf, t) }),
  };
}
